import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, Camera, Mic, Volume2, CheckCircle, ArrowLeft } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import type { Meeting } from "./CreateMeeting";

export default function MicTest() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMic, setSelectedMic] = useState<string>("");
  const [selectedCamera, setSelectedCamera] = useState<string>("");
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>("");
  
  const [cameraStatus, setCameraStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [micStatus, setMicStatus] = useState<"idle" | "loading" | "testing" | "success" | "error">("idle");
  const [cameraError, setCameraError] = useState("");
  const [micError, setMicError] = useState("");
  
  const [micLevel, setMicLevel] = useState(0);
  const [rememberSettings, setRememberSettings] = useState(false);

  // Load meeting from sessionStorage
  useEffect(() => {
    const pendingMeeting = sessionStorage.getItem("pendingMeeting");
    if (pendingMeeting) {
      setMeeting(JSON.parse(pendingMeeting));
    } else {
      toast.error("No meeting found. Please create a meeting first.");
      navigate("/create-meeting");
    }
  }, [navigate]);

  // Enumerate devices
  useEffect(() => {
    const getDevices = async () => {
      try {
        const deviceList = await navigator.mediaDevices.enumerateDevices();
        setDevices(deviceList);

        const audioInputs = deviceList.filter(d => d.kind === "audioinput");
        const videoInputs = deviceList.filter(d => d.kind === "videoinput");
        const audioOutputs = deviceList.filter(d => d.kind === "audiooutput");

        if (audioInputs.length > 0 && !selectedMic) setSelectedMic(audioInputs[0].deviceId);
        if (videoInputs.length > 0 && !selectedCamera) setSelectedCamera(videoInputs[0].deviceId);
        if (audioOutputs.length > 0 && !selectedSpeaker) setSelectedSpeaker(audioOutputs[0].deviceId);
      } catch (error) {
        console.error("Error enumerating devices:", error);
      }
    };

    getDevices();
  }, []);

  // Start camera
  const startCamera = async () => {
    setCameraStatus("loading");
    setCameraError("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: selectedCamera ? { exact: selectedCamera } : undefined },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      streamRef.current = stream;
      setCameraStatus("success");
      toast.success("Camera test successful!");
    } catch (error: any) {
      console.error("Camera error:", error);
      setCameraStatus("error");
      setCameraError(
        error.name === "NotAllowedError"
          ? "Camera permission denied. Please allow camera access in your browser settings."
          : "Failed to access camera. Please check your device and try again."
      );
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraStatus("idle");
  };

  // Start mic test
  const startMicTest = async () => {
    setMicStatus("loading");
    setMicError("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: selectedMic ? { exact: selectedMic } : undefined },
        video: false,
      });

      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      
      analyser.fftSize = 256;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateLevel = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        const normalizedLevel = Math.min(100, (average / 128) * 100);
        setMicLevel(normalizedLevel);

        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };

      updateLevel();
      setMicStatus("testing");
      toast.success("Mic test started! Speak to see the level indicator.");
    } catch (error: any) {
      console.error("Mic error:", error);
      setMicStatus("error");
      setMicError(
        error.name === "NotAllowedError"
          ? "Microphone permission denied. Please allow microphone access in your browser settings."
          : "Failed to access microphone. Please check your device and try again."
      );
    }
  };

  // Stop mic test
  const stopMicTest = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setMicLevel(0);
    setMicStatus("idle");
  };

  // Play test tone
  const playTestTone = () => {
    const audioContext = new AudioContext();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 440; // A4 note
    gainNode.gain.value = 0.3;

    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.5);

    toast.success("Playing test tone through selected speaker");
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
      stopMicTest();
    };
  }, []);

  const handleJoinMeeting = () => {
    if (cameraStatus !== "success" && micStatus !== "testing") {
      toast.error("Please test at least your camera or microphone before joining.");
      return;
    }

    // Clean up test streams
    stopCamera();
    stopMicTest();

    if (rememberSettings) {
      sessionStorage.setItem("deviceSettings", JSON.stringify({
        micId: selectedMic,
        cameraId: selectedCamera,
        speakerId: selectedSpeaker,
      }));
    }

    // For now, navigate to a placeholder meeting screen
    // In production, this would create the actual meeting and navigate to the meeting room
    toast.success("Devices verified! Starting meeting...");
    navigate("/meeting-placeholder", { state: { meeting, devicesVerified: true } });
  };

  const microphones = devices.filter(d => d.kind === "audioinput");
  const cameras = devices.filter(d => d.kind === "videoinput");
  const speakers = devices.filter(d => d.kind === "audiooutput");

  const canJoin = cameraStatus === "success" || micStatus === "testing";

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5">
      <Card className="w-full max-w-5xl">
        <CardHeader>
          <CardTitle className="text-2xl">Test your mic & camera</CardTitle>
          <CardDescription>
            Make sure everything works before you join {meeting?.title || "the meeting"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Camera Test */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Camera className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Camera Test</h3>
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Select Camera</Label>
                  <Select value={selectedCamera} onValueChange={setSelectedCamera}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose camera" />
                    </SelectTrigger>
                    <SelectContent>
                      {cameras.map(camera => (
                        <SelectItem key={camera.deviceId} value={camera.deviceId}>
                          {camera.label || `Camera ${camera.deviceId.slice(0, 5)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                  {cameraStatus === "idle" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-muted">
                      <Camera className="h-12 w-12 text-muted-foreground" />
                    </div>
                  )}
                  {cameraStatus === "success" && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white p-1 rounded-full">
                      <CheckCircle className="h-4 w-4" />
                    </div>
                  )}
                </div>

                {cameraError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{cameraError}</AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-2">
                  {cameraStatus === "success" ? (
                    <Button onClick={stopCamera} variant="outline" className="w-full">
                      Stop Camera
                    </Button>
                  ) : (
                    <Button
                      onClick={startCamera}
                      disabled={cameraStatus === "loading"}
                      className="w-full"
                    >
                      {cameraStatus === "loading" ? "Starting..." : "Start Camera"}
                    </Button>
                  )}
                  {cameraStatus === "error" && (
                    <Button onClick={startCamera} variant="outline" className="w-full">
                      Retry
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Microphone Test */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Mic className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Microphone Test</h3>
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Select Microphone</Label>
                  <Select value={selectedMic} onValueChange={setSelectedMic}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose microphone" />
                    </SelectTrigger>
                    <SelectContent>
                      {microphones.map(mic => (
                        <SelectItem key={mic.deviceId} value={mic.deviceId}>
                          {mic.label || `Microphone ${mic.deviceId.slice(0, 5)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Select Speaker</Label>
                  <Select value={selectedSpeaker} onValueChange={setSelectedSpeaker}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose speaker" />
                    </SelectTrigger>
                    <SelectContent>
                      {speakers.map(speaker => (
                        <SelectItem key={speaker.deviceId} value={speaker.deviceId}>
                          {speaker.label || `Speaker ${speaker.deviceId.slice(0, 5)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button onClick={playTestTone} variant="outline" className="w-full">
                  <Volume2 className="h-4 w-4 mr-2" />
                  Play Test Tone
                </Button>

                <div className="space-y-2 p-4 bg-muted rounded-lg">
                  <Label>Microphone Level</Label>
                  <Progress value={micLevel} className="h-3" />
                  <p className="text-xs text-muted-foreground">
                    {micStatus === "testing" ? "Speak to test your microphone" : "Start test to see levels"}
                  </p>
                </div>

                {micError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{micError}</AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-2">
                  {micStatus === "testing" ? (
                    <Button onClick={stopMicTest} variant="outline" className="w-full">
                      Stop Mic Test
                    </Button>
                  ) : (
                    <Button
                      onClick={startMicTest}
                      disabled={micStatus === "loading"}
                      className="w-full"
                    >
                      {micStatus === "loading" ? "Starting..." : "Start Mic Test"}
                    </Button>
                  )}
                  {micStatus === "error" && (
                    <Button onClick={startMicTest} variant="outline" className="w-full">
                      Retry
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Remember Settings */}
          <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
            <Checkbox
              id="remember"
              checked={rememberSettings}
              onCheckedChange={(checked) => setRememberSettings(checked as boolean)}
            />
            <Label htmlFor="remember" className="cursor-pointer">
              Remember my device settings for this session
            </Label>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/create-meeting")}
              className="sm:flex-1"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Meeting Details
            </Button>
            <Button
              onClick={handleJoinMeeting}
              disabled={!canJoin}
              className="sm:flex-1"
              size="lg"
            >
              Join Meeting
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
